import { computed, toValue } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * MY1-3040 — the single client-side interpretation of a welcome bonus's
 * `tracking` object (serialized by the MY1-3037 API on the wallet table,
 * card details and dashboard-widget payloads). Every surface renders from
 * this composable; none re-derives the state machine.
 *
 * Pure: accepts a plain object, ref or getter for a serialized welcome bonus
 * (or a tier / the widget's flattened tier pseudo-row) and does no fetching.
 * A row without a `tracking` object (stale cached payload) simply shows no
 * progress bar.
 */

// No bar at all — the status pill / received date carries the story.
const HIDDEN_STATES = ['not_auto_trackable', 'received', 'goal_not_met', 'suppressed'];

// Gray empty track, no fill, $0 label — "this could be tracked".
const EMPTY_STATES = ['connectable', 'spending_disabled'];

// Orange active bar.
const ACTIVE_STATES = ['tracking', 'goal_reached'];

const URGENT_WINDOW_DAYS = 30;

// Whole-dollar labels per the design — but never round in a way that lies
// about the goal (Devin, PR #1686): SPENT floors ($1,999.50 shows $1,999,
// not the target) and REMAINING ceils ($0.49 shows $1 to go, not $0).
const formatAmount = (amount, round = Math.round) => {
    if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
        return '--';
    }

    return round(Number(amount)).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
};

const formatSpent = (amount) => formatAmount(amount, Math.floor);
const formatRemaining = (amount) => formatAmount(amount, Math.ceil);

/**
 * MY1-3049 — hover copy shared across surfaces (currently the dashboard
 * widget). Pure functions over a serialized bonus/tier row so v-for surfaces
 * don't need a composable instance per row; null means "no tooltip", which
 * the hardened v-tooltip directive renders as nothing.
 */
export function bonusBarTooltip(row) {
    const tracking = row?.tracking ?? null;
    const state = tracking?.state ?? null;

    if (state === null || HIDDEN_STATES.includes(state)) {
        return null;
    }

    if (state === 'connectable') {
        return 'Connect bank to track bonus automatically';
    }

    if (state === 'spending_disabled') {
        return 'Turn on My Spending to track bonus automatically';
    }

    const minimum = Number(row?.minimum_spend_amount);
    const spent = Math.max(0, Number(tracking?.spend_amount ?? 0) || 0);
    const rawRemaining = tracking?.amount_remaining;
    const remaining = Math.max(
        0,
        rawRemaining !== null && rawRemaining !== undefined
            ? Number(rawRemaining) || 0
            : Number.isFinite(minimum)
                ? Math.max(0, minimum - spent)
                : 0
    );

    // Expired bars freeze: "$1,500 spent, $500 left" (design frame copy).
    if (state === 'deadline_passed') {
        return `${formatSpent(spent)} spent, ${formatRemaining(remaining)} left`;
    }

    if (remaining <= 0) {
        return `${formatSpent(spent)} spent, goal reached`;
    }

    return `${formatSpent(spent)} spent, ${formatRemaining(remaining)} to go`;
}

export function bonusDueDateTooltip(row) {
    const deadline = row?.minimum_spend_deadline;

    if (!deadline) {
        return null;
    }

    return `Due date: ${dayjs(deadline).format('MMM D, YYYY')}`;
}

/**
 * MY1-3074 — "is this bonus past its minimum spend deadline?", the one
 * predicate every action surface gates the missed-goal confirmation on.
 *
 * Compared in UTC on purpose (Devin, PR #1701). The endpoint that accepts the
 * confirmation tests `minimum_spend_deadline->lt(today())` against Laravel's
 * UTC clock, and `date:Y-m-d` puts a bare calendar date on the wire. Judging
 * that date by the browser's local day instead would disagree with the API for
 * the hours either side of UTC midnight: east of UTC the button would appear
 * while the API still rejects it, which is the very failure this ticket is
 * fixing. The deadline day itself is still in flight, matching the strict
 * `lt()` and the server's tracking-state resolution.
 */
export function bonusDeadlinePassed(row) {
    const deadline = row?.minimum_spend_deadline;

    return Boolean(deadline) && dayjs.utc(deadline).isBefore(dayjs.utc(), 'day');
}

const clampPercent = (value) => Math.min(100, Math.max(0, value));

/**
 * MY1-3041 — rolls a serialized welcome bonus (with optional MY1-3039 tiers)
 * up into what a list surface renders: the "x/y received" pill facts, the
 * active tier, the row the progress bar should track, and the due date.
 *
 * Same contract as useBonusTracking: plain object, ref or getter; no
 * fetching. `trackingRow` is null when no bar should render at all
 * (received, or every tier received) — the pill carries the story.
 */
export function useWelcomeBonusRollup(bonus) {
    const bonusRow = computed(() => toValue(bonus) ?? null);

    const tiersDisplay = computed(() => {
        const tiers = bonusRow.value?.tiers;

        if (!tiers?.length) {
            return null;
        }

        const receivedCount = tiers.filter((tier) => tier.received).length;

        return {
            label: `${receivedCount}/${tiers.length} received`,
            receivedCount,
            total: tiers.length,
            partial: receivedCount > 0 && receivedCount < tiers.length,
        };
    });

    const activeTier = computed(() => {
        const tiers = bonusRow.value?.tiers;

        if (!tiers?.length) {
            return null;
        }

        const flagged = tiers.find((tier) => tier.tracking?.is_active_tier);
        if (flagged) {
            return flagged;
        }

        // When the API flags tiers, none flagged means every tier has exited
        // tracking (Devin, PR #1880) — an earned or missed tier is not current.
        // Only an unflagged payload falls back to the first unreceived tier.
        const hasTierFlags = tiers.some(
            (tier) => typeof tier.tracking?.is_active_tier === 'boolean'
        );

        return hasTierFlags ? null : tiers.find((tier) => !tier.received) || null;
    });

    const trackingRow = computed(() => {
        if (!bonusRow.value || bonusRow.value.received) {
            return null;
        }

        if (tiersDisplay.value) {
            return activeTier.value;
        }

        return bonusRow.value;
    });

    const dueDate = computed(
        () =>
            activeTier.value?.minimum_spend_deadline ||
            bonusRow.value?.minimum_spend_deadline ||
            null
    );

    return { tiersDisplay, activeTier, trackingRow, dueDate };
}

export function useBonusTracking(bonus) {
    const row = computed(() => toValue(bonus) ?? {});
    const tracking = computed(() => row.value.tracking ?? null);

    const state = computed(() => tracking.value?.state ?? null);

    const showProgressBar = computed(
        () => state.value !== null && !HIDDEN_STATES.includes(state.value)
    );

    const isEmpty = computed(() => EMPTY_STATES.includes(state.value));
    const isDisabled = computed(() => state.value === 'deadline_passed');
    const isActive = computed(() => ACTIVE_STATES.includes(state.value));

    const minimumSpend = computed(() => {
        const minimum = Number(row.value.minimum_spend_amount);

        return Number.isFinite(minimum) && minimum > 0 ? minimum : null;
    });

    // The empty states render $0 by design — an opted-out or unconnected
    // card must never imply tracking that isn't happening. Floored at zero:
    // a stale negative manual spend (pre-validation rows) must not render
    // negative progress or inflate the remainder.
    const spentAmount = computed(() =>
        isEmpty.value
            ? 0
            : Math.max(0, Number(tracking.value?.spend_amount ?? 0) || 0)
    );

    const pendingAmount = computed(() =>
        isActive.value ? Number(tracking.value?.pending_spend_amount ?? 0) || 0 : 0
    );

    const remainingAmount = computed(() => {
        const remaining = tracking.value?.amount_remaining;

        if (remaining !== null && remaining !== undefined && !isEmpty.value) {
            // A stale negative spend inflates the server's remainder; never
            // show more left to spend than the minimum itself.
            return minimumSpend.value === null
                ? Math.max(0, Number(remaining) || 0)
                : Math.min(
                    Math.max(0, Number(remaining) || 0),
                    minimumSpend.value
                );
        }

        return minimumSpend.value === null
            ? 0
            : Math.max(0, minimumSpend.value - spentAmount.value);
    });

    const daysRemaining = computed(() => {
        const days = tracking.value?.days_remaining;

        return days === null || days === undefined ? null : Number(days);
    });

    const percent = computed(() => {
        if (isEmpty.value || minimumSpend.value === null) {
            return 0;
        }

        return clampPercent((spentAmount.value / minimumSpend.value) * 100);
    });

    // Pending (unposted) spend rides as a lighter segment after the posted
    // fill; it never counts toward the threshold.
    const pendingPercent = computed(() => {
        if (pendingAmount.value <= 0 || minimumSpend.value === null) {
            return 0;
        }

        return clampPercent((pendingAmount.value / minimumSpend.value) * 100 + percent.value) - percent.value;
    });

    const isUrgent = computed(
        () =>
            state.value === 'tracking' &&
            remainingAmount.value > 0 &&
            daysRemaining.value !== null &&
            daysRemaining.value >= 0 &&
            daysRemaining.value <= URGENT_WINDOW_DAYS
    );

    /**
     * Structured label pieces so surfaces can color the spent amount and the
     * secondary text independently, matching the design:
     *   active zero:     "$0 of $2,000 to go"
     *   active partial:  "$500 of $2,000"
     *   empty:           "$0 of $2,000"
     *   deadline passed: "$500" (frozen amount only)
     */
    const compactParts = computed(() => {
        const spent = formatSpent(spentAmount.value);

        if (isDisabled.value) {
            return { spent, of: null, target: null, suffix: null };
        }

        return {
            spent,
            of: 'of',
            target: formatAmount(minimumSpend.value),
            suffix: isActive.value && spentAmount.value === 0 ? 'to go' : null,
        };
    });

    const compactLabel = computed(() =>
        [compactParts.value.spent, compactParts.value.of, compactParts.value.target, compactParts.value.suffix]
            .filter(Boolean)
            .join(' ')
    );

    // The card details widget has enough space, so "of" is always spelled out
    // with both amounts, regardless of state.
    const verboseLabel = computed(
        () => `${formatSpent(spentAmount.value)} of ${formatAmount(minimumSpend.value)}`
    );

    const spentLabel = computed(() => `${formatSpent(spentAmount.value)} spent`);

    const remainingLabel = computed(() => {
        if (remainingAmount.value <= 0) {
            return '';
        }

        const toGo = `${formatRemaining(remainingAmount.value)} to go`;

        if (daysRemaining.value === null || daysRemaining.value < 0) {
            return toGo;
        }

        return `${toGo} in ${daysRemaining.value} ${daysRemaining.value === 1 ? 'day' : 'days'}`;
    });

    return {
        state,
        tracking,
        showProgressBar,
        isActive,
        isDisabled,
        isEmpty,
        isUrgent,
        percent,
        pendingPercent,
        spentAmount,
        remainingAmount,
        daysRemaining,
        compactParts,
        compactLabel,
        verboseLabel,
        spentLabel,
        remainingLabel,
    };
}
